import React, { forwardRef, useState } from 'react';
import { IconButton, InputAdornment, TextField, TextFieldProps, IconButtonProps } from '@mui/material';
import { HideIcon } from '@/src/components/icon/stroke/HideIcon';
import { ShowIcon } from '@/src/components/icon/stroke/ShowIcon';

export interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  /**
   * Tell browser how it should behave to password field
   * @default new-password
   */
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  autoComplete?: 'new-password' | 'current-password' | string;
  /**
   * Properties provided to button toggling visibility. Can be used to change aria-label.
   */
  iconButtonProps?: IconButtonProps;
  /**
   * Optional flag if password is visible
   * @default false
   */
  visible?: boolean;
  /**
   * Optional function to toggle visibility
   */
  setVisible?: (visible: boolean) => void;
}

/**
 * TextField adding extra logic to toggle into the password field via show/hide IconButton
 */
export const PasswordField: React.FC<PasswordFieldProps> = forwardRef(
  ({ slotProps, iconButtonProps, visible: propsVisible, setVisible: setPropsVisible, ...rest }, ref) => {
    const { input: inputSlotProps, ...restSlotProps } = slotProps || {};
    const [rawVisible, setRawVisible] = useState<boolean>(false);
    const visible = propsVisible ?? rawVisible;
    const setVisible = setPropsVisible ?? setRawVisible;

    return (
      <TextField
        ref={ref}
        type={visible ? 'text' : 'password'}
        autoComplete="new-password"
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setVisible(!visible)} aria-label="Toggle visibility" {...iconButtonProps}>
                  {visible ? <HideIcon /> : <ShowIcon />}
                </IconButton>
              </InputAdornment>
            ),
            ...inputSlotProps,
          },
          ...restSlotProps,
        }}
        {...rest}
      />
    );
  },
);
